import { Spinner } from '@blueprintjs/core'
import React, { Suspense, useMemo } from 'react'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { JSONElementFactory } from 'browser/components/json-elements/higher-order-components/json-element-factory'
import { resolveRefreshInterval } from '../ui-metric/metric-refresh'
import { CHART_TYPES, ChartType } from './chart-types'

// Keeps the Perses stack out of app startup: it is only evaluated when a ui:metricChart renders, so an
// import-time failure lands in PanelErrorBoundary instead of breaking boot. The webapp builds as one
// chunk (LimitChunkCountPlugin), so this does not reduce the download.
const PersesPanelHost = React.lazy(
  () => import(/* webpackChunkName: "perses-host" */ './perses-panel-host')
)

const DEFAULT_PAST_DURATION = '1d'
const PAST_DURATION_REGEX = /^(?:now-)?(\d+(?:ms|[smhdwy]))$/

interface IUIMetricChartProps extends IBaseProps {
  /** Chart to draw; defaults to 'timeSeries'. */
  chartType?: ChartType
  title?: string
  /** Single PromQL query, or several via `queries`. */
  query?: string
  queries?: string[]
  /** Series name template, e.g. '{{status}}'; defaults to the raw label set. */
  legendFormat?: string
  /** Chart-specific options (legend, thresholds, calculation, …); defaults to the chart's own. */
  panelSpec?: Record<string, unknown>
  /** Relative window: '5d', 'now-5d' or `{ from: 'now-5d' }`. */
  range?: string | { from?: string }
  /** Re-query interval: '30s', '5m', '1h'; '0s' disables. Defaults to '5m'. */
  refreshInterval?: string
  /** Panel height in px; defaults to 300. */
  height?: number
  // Injected by JSONElementFactory; declared so the factory's prop constraint fits.
  label?: unknown
  frames?: unknown
  errors?: unknown
  value?: unknown
  focusHotkey?: unknown
}

/** Perses throws on an invalid duration, so anything unrecognized falls back to the default. */
function toPastDuration(range: IUIMetricChartProps['range']): string {
  const expr = typeof range === 'string' ? range : range?.from
  const match = expr ? PAST_DURATION_REGEX.exec(expr.trim()) : null
  return match ? match[1] : DEFAULT_PAST_DURATION
}

interface IPanelErrorBoundaryState {
  hasError: boolean
}

interface IPanelErrorBoundaryProps extends IBaseProps {
  /** A change clears a caught error, so a corrected spec renders again. */
  resetKey: string
}

/** Keeps a Perses render error or a failed chunk load from unmounting the whole view. */
class PanelErrorBoundary extends React.Component<
  IPanelErrorBoundaryProps,
  IPanelErrorBoundaryState
> {
  public state: IPanelErrorBoundaryState = { hasError: false }

  public componentDidUpdate(prevProps: IPanelErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false })
    }
  }

  public static getDerivedStateFromError(): IPanelErrorBoundaryState {
    return { hasError: true }
  }

  public componentDidCatch(error: Error) {
    console.error('ui:metricChart failed to render', error)
  }

  public render() {
    if (this.state.hasError) {
      return <div className="pa3 u-textColorError">This chart could not be displayed.</div>
    }
    return this.props.children
  }
}

/** Charts a per-firm metric; `chartType` picks the chart, and the spec is authored inline on the node. */
const UIMetricChartComponent: React.FC<IUIMetricChartProps> = (props) => {
  const {
    chartType = 'timeSeries',
    title,
    query,
    queries: extraQueries,
    legendFormat,
    panelSpec,
    range,
    height = 300,
    className,
    refreshInterval,
  } = props
  const queries = useMemo(
    () => [query, ...(extraQueries ?? [])].filter((q): q is string => Boolean(q)),
    [query, extraQueries]
  )
  const pastDuration = toPastDuration(range)
  const refresh = resolveRefreshInterval(refreshInterval)
  const resetKey = useMemo(
    () =>
      JSON.stringify([chartType, queries, legendFormat, panelSpec, pastDuration, refresh, height]),
    [chartType, queries, legendFormat, panelSpec, pastDuration, refresh, height]
  )

  if (!(chartType in CHART_TYPES)) {
    return (
      <div className="pa3 u-textColorError">
        ui:metricChart: unsupported chartType &apos;{String(chartType)}&apos;
      </div>
    )
  }
  if (queries.length === 0) {
    return <div className="pa3 u-textColorMuted">ui:metricChart: no query</div>
  }

  return (
    <div className={className}>
      <PanelErrorBoundary resetKey={resetKey}>
        <Suspense fallback={<Spinner size={20} />}>
          {/* Keyed so a changed range or interval remounts with fresh time-range state. */}
          <PersesPanelHost
            key={`${pastDuration}|${refresh}`}
            panelKind={CHART_TYPES[chartType]}
            title={title}
            queries={queries}
            legendFormat={legendFormat}
            panelSpec={panelSpec}
            pastDuration={pastDuration}
            refreshInterval={refresh}
            height={height}
          />
        </Suspense>
      </PanelErrorBoundary>
    </div>
  )
}

export const UIMetricChart = JSONElementFactory(UIMetricChartComponent)
